import i18next from 'i18next';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import {
  pullDrawingsTabStatusSuccess,
  pullDrawingsTabStatusFailure,
  pullDrawingsTabStatus,
  navigateToWixel,
} from '../../actions/studio/playground';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { sanitizeUrl } from '@wix/da-url';
import { MURO_FOLDER_NAME } from '../../../constants';
import type {
  PapiStudioPagesPlayground,
  PapiStudioWixelLink,
} from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import { PlaygroundSubsection } from '../../types/store';
import { getPlaygroundSubsection } from '../../selectors/studio';

function* handlePullDrawingsTabStatus() {
  const playgroundSubsection = yield select(getPlaygroundSubsection);
  // We need to determine if muro's folder should be visible in the tab navigation.
  // If we are already on the muro page, we can assume it should be shown.
  if (playgroundSubsection === PlaygroundSubsection.Draw) {
    yield put(pullDrawingsTabStatusSuccess(true));
    return;
  }

  // Otherwise, we need to fire a request to find out.
  const response: PapiStudioPagesPlayground = yield call(
    requestPuppy,
    {
      url: 'pages/playground',
      method: 'GET',
      params: {
        root_foldername: MURO_FOLDER_NAME,
        init: false,
      },
    },
    undefined,
    'studio'
  );

  if (isApiError(response)) {
    ddt.error(
      'playground',
      'Failed to check muro folder status in studio playground due to API error',
      response
    );
    yield put(pullDrawingsTabStatusFailure());
  } else {
    let isDrawingsTabVisible = false;
    if (
      response.rootFolder?.size !== undefined &&
      response.rootFolder.size > 0
    ) {
      isDrawingsTabVisible = true;
    }

    ddt.info(
      'playground',
      isDrawingsTabVisible
        ? 'Muro folder detected, showing "Drawings" tab'
        : 'Muro folder not detected, hiding "Drawings" tab',
      response
    );

    yield put(pullDrawingsTabStatusSuccess(isDrawingsTabVisible));
  }
}

function* handleNavigateToWixel() {
  const response: PapiStudioWixelLink = yield call(
    requestPuppy,
    {
      url: 'wixel/link',
      method: 'GET',
    },
    undefined,
    'studio'
  );

  if (!response || isApiError(response) || !response.link) {
    ddt.error('playground', 'Failed to pull Wixel link', response);
    yield putErrorNotification(
      i18next.t('studio.header.tabs.playground.subtabs.wixel.error')
    );
    return;
  }

  const safeLink = sanitizeUrl(response.link);
  if (safeLink) {
    window.open(safeLink, '_blank');
  }
}

export default function* saga() {
  yield all([
    takeLatest(pullDrawingsTabStatus, handlePullDrawingsTabStatus),
    takeLatest(navigateToWixel, handleNavigateToWixel),
  ]);
}
